/**
 * Type definitions for the consolidated entity-badge UI primitives.
 *
 * These types are the contract between the UI primitives (`EntityBadge`,
 * `EntityBadgeGroup`) and the wrappers that hydrate them. The UI layer
 * never reads from the store, never fetches, and never knows about
 * associations — it just renders the supplied state.
 *
 * See issue #5032 for the consolidation rationale.
 */

/**
 * Semantic colour for the optional status dot rendered on the avatar.
 *
 * Maps to the Untitled-UI `utility-*` tokens so dark mode is handled by
 * the design system. `neutral` is intentional (vs. `gray`) to keep the
 * vocabulary semantic — callers describe meaning, not colour.
 */
export type EntityBadgeStatusColor =
  | 'success'
  | 'warning'
  | 'error'
  | 'neutral';

/**
 * Optional status indicator rendered as a small dot at the bottom-right
 * of the badge's avatar (only when `avatar` is enabled — there's no
 * sensible position when the badge has just a leading icon).
 */
export interface EntityBadgeStatus {
  /** Semantic colour for the dot. */
  color: EntityBadgeStatusColor;
  /**
   * Accessible description of the status — read by screen readers and
   * shown as a tooltip. e.g. "Accepted", "Online", "Out of office".
   */
  label: string;
}

/**
 * Pre-resolved data needed to render a single entity badge.
 *
 * Wrappers are responsible for converting `CrmObject`s, search results,
 * etc. into this shape and choosing the best `avatarUrl` (e.g. profile
 * photo → Gravatar → favicon → undefined).
 */
export interface EntityBadgeData {
  /** Entity UUID — used as the click identifier and (when `key` is omitted) the React key. */
  id: string;
  /**
   * Optional stable React key. Use when the same `id` may legitimately
   * appear more than once in a group (e.g. the same entity surfacing in
   * multiple roles like From/To/Cc on an email thread). When omitted,
   * `EntityBadgeGroup` falls back to `id` as the key.
   */
  key?: string;
  /** Entity type name, e.g. `ahoy_contact`, `ahoy_company`. */
  entityType: string;
  /** Pre-resolved display name. */
  displayName: string;
  /** Optional pre-resolved avatar URL. The primitive falls back to initials/icon when omitted. */
  avatarUrl?: string;
  /**
   * Optional status indicator rendered as a coloured dot overlaid on the
   * avatar. Only renders when `avatar` is enabled on the badge. Use for
   * presence (online/offline), RSVP state, lifecycle status, etc.
   */
  status?: EntityBadgeStatus;
  /**
   * Optional email address for badges that represent a person. Populated for
   * BOTH resolved contacts/users (e.g. `ParticipantPillList` passes the
   * entity's primary email through) and unresolved raw-email participants.
   * Consumers read it for display (`showEmail` renders `Name (email)`) and
   * the hover popover body uses it for the email row + "Draft email" CTA.
   * Omit for non-person badges (companies, deals).
   */
  email?: string;
  /**
   * Marks the badge as part of a pending change — a proposal that adds or
   * removes this association. Renders a leading +/− glyph plus screen-reader
   * text, and de-emphasizes the badge when removed. Omit for settled data.
   */
  changeMarker?: EntityBadgeChangeMarker;
}

/**
 * A pending change to an association, shown per badge.
 *
 * Deliberately per-target rather than a "replaced by" pairing: a record can
 * hold several associations of the same type, so an addition does not imply
 * the existing one goes away. Marking each end independently lets a move read
 * as one removal + one addition, and a pure addition read as an addition.
 */
export type EntityBadgeChangeMarker = 'added' | 'removed';

/**
 * Visual variant for entity badges.
 *
 * - `default` — full pill, used in record views, association rows,
 *   anywhere a standalone badge is appropriate.
 * - `inline` — baseline-aligned variant for use inside flowing text
 *   (markdown bodies, notes, reasoning chat, proposal copy).
 * - `xs` — compact variant for dense lists / table cells.
 * - `record` — the row's own record: a 24px avatar and a bold label, with no
 *   pill around it. **Opt-in.** The other three variants say "here is a record
 *   referenced from somewhere"; `record` says "this cell IS what the row is
 *   about", which is why it drops the pill — a table whose every cell wears one
 *   reads as a wall of chips.
 *
 *   The test is what the row is *about*, not whether the record is "related":
 *     - a CRM list row is about its record → its `object_name` column
 *     - a report row is a GROUP BY, so it is about its group → the grouped
 *       entity dimension (`SUM(amount) BY company` → the company cell)
 *   Anything the row merely mentions — an association column beside the name,
 *   a company listed on a contact row — stays `xs`. That contrast is the
 *   point, and reaching for `record` because it looks nicer erases it.
 */
export type EntityBadgeVariant = 'default' | 'inline' | 'xs' | 'record';

/**
 * The subset of variants a *group* of badges can be rendered in.
 *
 * `record` is excluded: it means "this is the record the row is about", and a
 * row is about exactly one record, so a group of them is a category error. The
 * exclusion lives in the type so `<EntityBadgeGroup variant='record'>` fails to
 * compile rather than rendering a plausible-looking wall of 24px avatars.
 */
export type EntityBadgeGroupVariant = Exclude<EntityBadgeVariant, 'record'>;

/**
 * `vertical-align` for a badge's outermost inline-level box, per variant.
 *
 * Lives here, next to the variant vocabulary, because the badge is not the only
 * thing that needs it: `EntityBadgeWithHoverPreview` wraps every badge in a span
 * of its own, and *that* span — not the badge — is the inline-level box in the
 * surrounding line box. `vertical-align` has no effect on a flex item, so the
 * wrapper wins by construction and a wrapper that decides alignment for itself
 * silently outvotes the badge. One map, every consumer.
 *
 * `inline` and `record` are middle-aligned because they sit in a line box whose
 * height they do not control (prose, a table cell), and baseline alignment there
 * makes that line box depend on the badge's *content*: an `<img>`'s baseline is
 * its bottom edge, whereas a text span's is its own baseline. So a company with
 * a resolvable logo sat a full 24px above the baseline plus the label's
 * descender, while one falling back to initials tucked in underneath — 47px and
 * 43px rows in the same report table, for a reason invisible in the markup. The
 * pill variants keep the default: they are laid out by flex parents, which
 * ignore `vertical-align` anyway.
 */
export const WRAPPER_ALIGN_CLASSES: Record<EntityBadgeVariant, string> = {
  default: '',
  inline: 'align-middle',
  xs: '',
  record: 'align-middle',
};

/**
 * Render state for `EntityBadge` (single).
 *
 * Encoded as a discriminated union so it is impossible to pass `data`
 * and `loading` simultaneously — that mistake was a recurring bug in
 * the legacy components.
 */
export type EntityBadgeState =
  | { kind: 'data'; data: EntityBadgeData }
  | { kind: 'loading' }
  | {
      kind: 'failed';
      /**
       * Optional fallback label shown when the entity could not be loaded.
       * Wrappers pass something the user can recognise (e.g. an email
       * address for `EntityBadgeByEmail`); the primitive shows a generic
       * "Unavailable" label when omitted.
       */
      fallbackLabel?: string;
      /**
       * When supplied, the badge renders as a non-interactive gray pill using
       * this data instead of the red "Unavailable" treatment. Used by markdown
       * rendering where the inline tag carries a display name and entity type
       * even when the entity cannot be hydrated. Takes visual precedence over
       * `fallbackLabel` when present.
       */
      fallbackData?: EntityBadgeData;
    };

/**
 * Render state for `EntityBadgeGroup` (multi).
 *
 * `partial` covers the case where some ids hydrated and others failed —
 * the loaded entities still render and a `+N failed` pill in the error
 * semantic tokens makes the discrepancy discoverable without being loud.
 */
export type EntityBadgeGroupState =
  | { kind: 'data'; data: EntityBadgeData[] }
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'partial'; loaded: EntityBadgeData[]; failedCount: number };

/**
 * Click behavior for entity badges. Wrappers translate this into a
 * concrete `onClick` for the UI primitive; UI primitives only ever
 * receive a callback, never a `ClickBehavior` value.
 */
export type ClickBehavior = 'slideout' | 'page' | 'none';
